import React, { useState, useEffect, useRef } from "react";
import {
  listTemplates,
  getPresignedUrl,
  uploadFileToPresignedUrl,
  confirmTemplateUpload,
  updateTemplate,
  deleteTemplate,
  exportWithTemplate,
  getExportErrorMessage,
} from "../api/exportTemplateAPI";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";

function ExportProjectDataModal({ isOpen, onClose, projectId }) {
  const { toast } = useToast();
  const confirmDialog = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createStep, setCreateStep] = useState(null); // null | 'name' | 'upload'
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateFile, setNewTemplateFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateName, setEditingTemplateName] = useState("");
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const [exportingTemplateId, setExportingTemplateId] = useState(null);
  const [renamingTemplateId, setRenamingTemplateId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchTemplates = async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listTemplates(projectId);
      setTemplates(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to load templates.");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && projectId) {
      fetchTemplates();
      setCreateStep(null);
      setNewTemplateName("");
      setNewTemplateFile(null);
      setError("");
      setEditingTemplateId(null);
      setDeletingTemplateId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen, projectId]);

  const handleClose = () => {
    setCreateStep(null);
    setNewTemplateName("");
    setNewTemplateFile(null);
    setError("");
    setEditingTemplateId(null);
    setEditingTemplateName("");
    onClose();
  };

  const handleCreateStart = () => {
    setCreateStep("name");
    setNewTemplateName("");
    setNewTemplateFile(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleNameNext = () => {
    const name = (newTemplateName || "").trim();
    if (!name) {
      setError("Template name is required and cannot be blank.");
      return;
    }
    setError("");
    setCreateStep("upload");
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      setError("Only Excel files (.xlsx, .xls) are allowed.");
      return;
    }
    setError("");
    setNewTemplateFile(file);
  };

  const handleUploadTemplate = async () => {
    const name = (newTemplateName || "").trim();
    const file = newTemplateFile;
    if (!name) {
      setError("Template name is required and cannot be blank.");
      return;
    }
    if (!file) {
      setError("Please select an Excel file to upload.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const { template_id, presigned_url, content_type } = await getPresignedUrl(projectId, {
        file_name: file.name,
        name,
      });
      await uploadFileToPresignedUrl(presigned_url, file, content_type);
      await confirmTemplateUpload(projectId, template_id);
      setNewTemplateName("");
      setNewTemplateFile(null);
      setCreateStep(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to upload template.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (templateId) => {
    const ok = await confirmDialog({ title: "Delete this template?", confirmLabel: "Delete" });
    if (!ok) return;
    setDeletingTemplateId(templateId);
    try {
      await deleteTemplate(projectId, templateId);
      await fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to delete template.");
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleRenameStart = (template) => {
    setEditingTemplateId(template.id);
    setEditingTemplateName(template.name || "");
  };

  const handleRenameSave = async () => {
    const name = (editingTemplateName || "").trim();
    if (!name) {
      setError("Template name is required and cannot be blank.");
      return;
    }
    if (!editingTemplateId) return;
    setRenamingTemplateId(editingTemplateId);
    try {
      await updateTemplate(projectId, editingTemplateId, { name });
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === editingTemplateId ? { ...t, name, updated_at: new Date().toISOString() } : t
        )
      );
      setEditingTemplateId(null);
      setEditingTemplateName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to rename template.");
    } finally {
      setRenamingTemplateId(null);
    }
  };

  const handleExportWithTemplate = async (templateId) => {
    setExportingTemplateId(templateId);
    try {
      const { presigned_url, file_name } = await exportWithTemplate(projectId, templateId);
      const downloadResponse = await fetch(presigned_url);
      if (!downloadResponse.ok) {
        throw new Error("Failed to download export file.");
      }
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file_name || `project_export_${templateId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = await getExportErrorMessage(err);
      toast.error(msg);
    } finally {
      setExportingTemplateId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-slate-700 bg-clip-text text-transparent flex-1">
              Export Project Data
            </h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {createStep === null && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Choose a template to export your project data, or create a new template by uploading an Excel file.
              </p>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-slate-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              ) : (
                <>
                  <p className="font-bold text-gray-900 mb-2">Existing Templates</p>
                  <ul className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                    {templates.length === 0 ? (
                      <li className="text-sm text-gray-500 py-4 text-center">No templates yet. Create one below.</li>
                    ) : (
                      templates.map((template) => (
                        <li
                          key={template.id}
                          className="flex items-center justify-between gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50/50"
                        >
                          {editingTemplateId === template.id ? (
                            <div className="flex-1 flex items-center gap-2">
                              <input
                                type="text"
                                value={editingTemplateName}
                                onChange={(e) => setEditingTemplateName(e.target.value)}
                                className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                                placeholder="Template name"
                              />
                              <button
                                onClick={handleRenameSave}
                                disabled={renamingTemplateId !== null}
                                className="px-2 py-1.5 bg-slate-600 text-white rounded text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                              >
                                {renamingTemplateId === template.id ? (
                                  <>
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Saving
                                  </>
                                ) : (
                                  "Save"
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingTemplateId(null);
                                  setEditingTemplateName("");
                                }}
                                disabled={renamingTemplateId !== null}
                                className="px-2 py-1.5 bg-gray-200 text-gray-700 rounded text-sm disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-gray-900 truncate block">{template.name}</span>
                                <span className="text-xs text-gray-500 capitalize">{template.status}</span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => handleExportWithTemplate(template.id)}
                                  disabled={template.status !== "completed" || exportingTemplateId !== null}
                                  className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                  title="Export with this template"
                                >
                                  {exportingTemplateId === template.id ? (
                                    <>
                                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                      </svg>
                                      Downloading…
                                    </>
                                  ) : (
                                    "Download"
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRenameStart(template)}
                                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Rename"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(template.id)}
                                  disabled={deletingTemplateId !== null}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                  title="Delete"
                                >
                                  {deletingTemplateId === template.id ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </>
                          )}
                        </li>
                      ))
                    )}
                  </ul>
                  <button
                    onClick={handleCreateStart}
                    className="w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-700 font-medium hover:border-slate-500 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create new template
                  </button>
                </>
              )}
            </>
          )}

          {createStep === "name" && (
            <>
              <p className="text-sm text-gray-600 mb-4">Give a name to your export template (required).</p>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
              )}
              <label className="block text-sm font-bold text-gray-700 mb-2">Template name *</label>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g. Q1 Export Template"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-base mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setCreateStep(null);
                    setError("");
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium"
                >
                  Back
                </button>
                <button
                  onClick={handleNameNext}
                  className="px-4 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700"
                >
                  Next
                </button>
              </div>
            </>
          )}

          {createStep === "upload" && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Upload an Excel file (.xlsx or .xls). This file defines the format for your export.
              </p>
              <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <span className="text-xs text-gray-500">Template name</span>
                <p className="font-medium text-gray-900">{newTemplateName.trim()}</p>
              </div>
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
              )}
              <label className="block text-sm font-bold text-gray-700 mb-2">Excel file *</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={uploading}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setCreateStep("name");
                    setError("");
                    setNewTemplateFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={uploading}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleUploadTemplate}
                  disabled={uploading || !newTemplateFile}
                  className="px-4 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  Upload template
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExportProjectDataModal;
